import Button from 'app/shared/components/button/Button';
import AddImageAndPreview from 'app/shared/components/form-input/AddImageAndPreview';
import Icon from 'app/shared/components/icon/Icon';
import IconLib from 'app/shared/components/icon/icon-lib';
import LoadingOverlay from 'app/shared/page-parts/LoadingOverlay';
import styledMixins from 'app/styles/styledMixins';
import { Classes } from 'app/tools/general.types';
import { useObservableSend } from 'app/tools/hooks/misc-hooks';
import clsx from 'clsx';
import { ReactNode, useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { BehaviorSubject } from 'rxjs';
import AppImage from './AppImage';

type Props = {
  alt: string;
  currentSrc?: string;
  loadingImg?: boolean;
  fallbackImgNode?: ReactNode;
  usedCustomImg?: boolean;
  imageSet$: BehaviorSubject<File | null>;
  className?: string;
  classes?: Classes<'appImage' | 'img'>;
  removeImage?: () => void;
};

export default function EditableImage({
  alt,
  currentSrc,
  loadingImg,
  fallbackImgNode,
  usedCustomImg,
  imageSet$,
  className,
  classes,
  removeImage,
}: Props): JSX.Element {
  const [imgLoaded, setImgLoaded] = useState(true);
  const [triggerImgSelect, imageSelectTrigger$] = useObservableSend();
  const { width, ref } = useResizeDetector();

  const handleImageSelect = (img: File | null) => {
    setImgLoaded(false);
    imageSet$.next(img);
  };

  useEffect(() => {
    if (!loadingImg && !currentSrc) setImgLoaded(true);
  }, [currentSrc, loadingImg]);

  const loading = loadingImg || !imgLoaded;
  const noCustomImgSet = loading || !usedCustomImg;

  return (
    <>
      <div
        className={clsx('relative self-center', className)}
        {...{ ref }}
      >
        <AppImage
          src={currentSrc}
          onLoad={() => setImgLoaded(true)}
          onFallbackUsed={() => setImgLoaded(true)}
          noPad
          css={styledMixins.collapseFix}
          className={classes?.appImage}
          classes={{ img: classes?.img }}
          {...{ alt, fallbackImgNode }}
        />
        <LoadingOverlay
          noPortal
          translucent
          color="secondary"
          size={width ? width * 0.6 : undefined}
          loadingComplete={!loading}
        />
        <AddImageAndPreview
          noPreview
          onFileSelected={handleImageSelect}
          promptForImage$={imageSelectTrigger$}
        />
        <Button
          plain
          noRadius
          color="primary"
          onClick={() => triggerImgSelect()}
          aria-label={
            noCustomImgSet
              ? 'add custom product image'
              : 'change custom product image'
          }
          className={clsx(
            'absolute -right-1 -bottom-1 w-3 h-3 rounded-full',
            'border border-white border-solid min-w-unset',
            loading && 'hidden'
          )}
        >
          <Icon icon={IconLib.EDIT} size={18} />
        </Button>
        <Button
          plain
          noRadius
          color="secondary"
          // onClick={() => handleImageSelect(null)}
          onClick={removeImage}
          aria-label="remove custom product image"
          className={clsx(
            'absolute -top-1 -right-1 w-3 h-3 rounded-full',
            'border border-white border-solid min-w-unset',
            noCustomImgSet && 'hidden'
          )}
        >
          <Icon icon={IconLib.CROSS} size={18} />
        </Button>
      </div>
    </>
  );
}
